import React, { useCallback, useEffect, useState } from "react";
import { ArrowRight, Check, AlertCircle, BadgeCheck } from "lucide-react";
import { ScreenHeader } from "@/components/ScreenHeader";
import { api } from "@/api/client";
import { colors, spacing, fonts } from "@/theme";

function fmt(iso) {
  const d = new Date(iso);
  return `${d.toLocaleDateString()} ${d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}`;
}

export default function Pending() {
  const [moves, setMoves] = useState([]);
  const [yards, setYards] = useState([]);
  const [yardFilter, setYardFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [ackingId, setAckingId] = useState(null);

  const load = useCallback(async () => {
    try {
      const data = await api("/moves?status=pending");
      setMoves(data);
    } catch (e) { console.error(e); }
  }, []);

  useEffect(() => {
    setLoading(true);
    load().finally(() => setLoading(false));
    (async () => {
      try {
        const r = await api("/locations");
        setYards(r.yards || []);
      } catch (e) { console.error(e); }
    })();
  }, [load]);

  useEffect(() => {
    const i = setInterval(load, 10000);
    return () => clearInterval(i);
  }, [load]);

  const ack = async (id) => {
    setAckingId(id);
    try {
      await api(`/moves/${id}/acknowledge`, { method: "POST" });
      await load();
    } catch (e) { console.error(e); } finally { setAckingId(null); }
  };

  const filtered = yardFilter === "all" ? moves : moves.filter((m) => m.yard === yardFilter);
  const countByYard = (y) => moves.filter((m) => m.yard === y).length;

  return (
    <div style={{ flex: 1, backgroundColor: colors.surface }}>
      <ScreenHeader title="Pending Moves" subtitle={`${filtered.length} to complete${yardFilter !== "all" ? ` · ${yardFilter}` : ""}`} />
      <div style={s.filterRow}>
        <button data-testid="yard-all" onClick={() => setYardFilter("all")} style={{ ...s.chip, ...(yardFilter === "all" ? s.chipActive : {}) }}>
          <span style={{ ...s.chipText, color: yardFilter === "all" ? colors.onSurfaceInverse : colors.onSurface }}>All yards</span>
          <span style={{ ...s.badge, ...(yardFilter === "all" ? s.badgeActive : {}) }}>
            <span style={{ ...s.badgeText, color: yardFilter === "all" ? colors.onBrandPrimary : colors.onBrandTertiary }}>{moves.length}</span>
          </span>
        </button>
        {yards.map((y) => (
          <button key={y} data-testid={`yard-${y}`} onClick={() => setYardFilter(y)} style={{ ...s.chip, ...(yardFilter === y ? s.chipActive : {}) }}>
            <span style={{ ...s.chipText, color: yardFilter === y ? colors.onSurfaceInverse : colors.onSurface }}>{y}</span>
            <span style={{ ...s.badge, ...(yardFilter === y ? s.badgeActive : {}) }}>
              <span style={{ ...s.badgeText, color: yardFilter === y ? colors.onBrandPrimary : colors.onBrandTertiary }}>{countByYard(y)}</span>
            </span>
          </button>
        ))}
      </div>

      {loading ? (
        <div style={s.spinnerWrap}><div className="yo-spinner" /></div>
      ) : filtered.length === 0 ? (
        <div style={s.emptyWrap}>
          <BadgeCheck size={64} color={colors.success} />
          <div style={s.emptyTitle}>All caught up</div>
          <div style={s.emptyText}>No pending moves. Enjoy the break.</div>
        </div>
      ) : (
        <div style={s.grid}>
          {filtered.map((item) => (
            <div key={item.id} data-testid={`pending-${item.id}`} style={s.card}>
              <div style={s.cardTop}>
                <div style={s.trailer}>{item.trailer_number}</div>
                {item.yard ? <div style={s.yardBadge}><span style={s.yardBadgeText}>{item.yard}</span></div> : null}
              </div>
              <div style={s.route}>
                <div style={s.locBox}>
                  <div style={s.locLabel}>From</div>
                  <div style={s.locVal}>{item.from_location}</div>
                </div>
                <ArrowRight size={28} color={colors.brandPrimary} style={{ flexShrink: 0 }} />
                <div style={s.locBox}>
                  <div style={s.locLabel}>To</div>
                  <div style={s.locVal}>{item.to_location}</div>
                </div>
              </div>
              <div style={s.meta}>Dispatch · {item.created_by_name} · {fmt(item.created_at)}</div>
              {item.notes && item.notes.trim().length > 0 ? (
                <div style={s.notesBox} data-testid={`notes-${item.id}`}>
                  <div style={s.notesHeader}>
                    <AlertCircle size={16} color={colors.warning} />
                    <span style={s.notesLabel}>Dispatch note</span>
                  </div>
                  <div style={s.notesText}>{item.notes.trim()}</div>
                </div>
              ) : null}
              <button data-testid={`ack-${item.id}`} onClick={() => ack(item.id)} disabled={ackingId === item.id}
                style={{ ...s.ackBtn, ...(ackingId === item.id ? { opacity: 0.6 } : {}) }}>
                {ackingId === item.id ? (
                  <div className="yo-spinner" style={{ width: 24, height: 24, borderWidth: 3 }} />
                ) : (
                  <>
                    <Check size={24} color={colors.onBrandPrimary} strokeWidth={3} />
                    <span style={s.ackText}>Acknowledge</span>
                  </>
                )}
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const s = {
  filterRow: { display: "flex", flexWrap: "wrap", gap: spacing.sm, padding: `${spacing.md}px ${spacing.xl}px ${spacing.sm}px` },
  chip: { display: "flex", alignItems: "center", gap: spacing.xs, padding: `10px ${spacing.md}px`, borderWidth: 2, borderStyle: "solid", borderColor: colors.onSurface, backgroundColor: colors.surfaceSecondary, cursor: "pointer" },
  chipActive: { backgroundColor: colors.onSurface },
  chipText: { ...fonts.monoBold, fontSize: 11, letterSpacing: 0 },
  badge: { minWidth: 22, padding: "1px 6px", borderWidth: 1, borderStyle: "solid", borderColor: colors.onSurface, backgroundColor: colors.brandTertiary, textAlign: "center" },
  badgeActive: { backgroundColor: colors.brandPrimary, borderColor: colors.brandPrimary },
  badgeText: { ...fonts.monoBold, fontSize: 10, letterSpacing: 0 },
  spinnerWrap: { display: "flex", justifyContent: "center", marginTop: 40 },
  grid: { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(340px, 1fr))", gap: spacing.md, padding: spacing.xl },
  card: { borderWidth: 2, borderStyle: "solid", borderColor: colors.onSurface, backgroundColor: colors.surfaceSecondary, padding: spacing.lg },
  cardTop: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: spacing.md },
  trailer: { ...fonts.monoBold, fontSize: 28, color: colors.onSurface },
  yardBadge: { backgroundColor: colors.brandPrimary, padding: `4px ${spacing.sm}px`, borderWidth: 2, borderStyle: "solid", borderColor: colors.brandPrimary },
  yardBadgeText: { ...fonts.monoBold, fontSize: 11, color: colors.onBrandPrimary, letterSpacing: 0 },
  route: { display: "flex", alignItems: "center", gap: spacing.md, marginBottom: spacing.md },
  locBox: { flex: 1, borderWidth: 2, borderStyle: "solid", borderColor: colors.border, padding: spacing.sm, backgroundColor: colors.surface },
  locLabel: { ...fonts.monoBold, fontSize: 9, color: colors.onSurfaceTertiary, letterSpacing: 0 },
  locVal: { ...fonts.monoBold, fontSize: 16, color: colors.onSurface, marginTop: 2 },
  meta: { ...fonts.mono, fontSize: 11, color: colors.onSurfaceTertiary, marginBottom: spacing.md },
  notesBox: { borderWidth: 2, borderStyle: "solid", borderColor: colors.warning, backgroundColor: colors.onWarning, padding: spacing.sm, marginBottom: spacing.md },
  notesHeader: { display: "flex", alignItems: "center", gap: spacing.xs, marginBottom: 4 },
  notesLabel: { ...fonts.monoBold, fontSize: 10, color: colors.warning, letterSpacing: 0 },
  notesText: { ...fonts.mono, fontSize: 13, color: colors.onSurface, lineHeight: 1.4 },
  ackBtn: { display: "flex", alignItems: "center", justifyContent: "center", gap: spacing.sm, width: "100%", backgroundColor: colors.brandPrimary, padding: 22, minHeight: 64, borderWidth: 2, borderStyle: "solid", borderColor: colors.brandPrimary, cursor: "pointer" },
  ackText: { color: colors.onBrandPrimary, ...fonts.display, fontSize: 18 },
  emptyWrap: { display: "flex", flexDirection: "column", alignItems: "center", marginTop: 64, gap: spacing.md },
  emptyTitle: { ...fonts.display, fontSize: 22, color: colors.onSurface },
  emptyText: { ...fonts.mono, color: colors.onSurfaceTertiary, fontSize: 12 },
};
